import { faFileDownload } from '@fortawesome/pro-regular-svg-icons';
import type { TFunction } from 'i18next';
import { toJS } from 'mobx';

import type {
  Campaign,
  DisplayCreative,
  FacebookDisplayCreative,
  IBannersnackUrl,
} from '@feathr/blackbox';
import { CampaignState, isBannersnackCreative, isMetaBlockedDimension } from '@feathr/blackbox';
import type { ICreativeCardMenuItem } from '@feathr/components';
import { validateAdTagCreative } from '@feathr/extender/components/creatives/CreativeModal/CreativeModal.utils';
import { hasValidAspectRatio } from '@feathr/extender/components/creatives/dimensionValidation.utils';
import {
  extractMetaFeedback,
  getDisplayCreativeStatus,
  getMetaAdStatusLabel,
  getMetaPortraitCropWarning,
  mapMetaAdStatusToVisual,
} from '@feathr/extender/components/creatives/status.utils';

export type TCreativeStatus = 'ready' | 'warning' | 'error';

export interface ICreativeStatusInfo {
  /** Undefined when no status data exists yet (hides the status icon) */
  status?: TCreativeStatus;
  errors: string[];
}

/**
 * Derives the visual status and collects all error/warning messages for a creative
 * in a single pass. Validation errors always produce 'error'; if none, ad_status
 * determines the result.
 *
 * @param creative - The creative to evaluate
 * @param campaign - The campaign the creative belongs to
 * @param t - Translation function
 * @returns Status and array of error/warning messages
 */
export function getCreativeStatusInfo(
  creative: DisplayCreative,
  campaign: Campaign,
  t: TFunction,
): ICreativeStatusInfo {
  const validationErrors = getValidationErrors(creative, campaign, t);
  const errors = [...validationErrors];
  const hasValidationErrors = validationErrors.length > 0;

  if (creative.channel === 'meta') {
    const fbCreative = creative as FacebookDisplayCreative;
    const adStatus = fbCreative.get('ad_status');
    const isBlockedPortrait = isMetaBlockedDimension(creative.get('width'), creative.get('height'));

    if (isBlockedPortrait) {
      errors.push(getMetaPortraitCropWarning(t));
    }

    // Add ad_status errors if present
    if (adStatus && adStatus !== 'ACTIVE') {
      const label = getMetaAdStatusLabel(adStatus, t);
      const feedback = extractMetaFeedback(fbCreative.get('ad_review_feedback'));
      errors.push(feedback ? `${label}: ${feedback}` : label);
    }

    // Determine status: validation errors take priority, then ad_status.
    let status: TCreativeStatus | undefined;
    if (hasValidationErrors) {
      status = 'error';
    } else if (adStatus) {
      status = mapMetaAdStatusToVisual(adStatus);
    } else if (campaign.get('state') === CampaignState.Published) {
      // No ad_status yet: show pending once published, otherwise hide status.
      errors.push(t('Pending review'));
      status = 'warning';
    } else {
      status = undefined;
    }

    // A blocked 9:16 portrait is at least a warning, even when the ad is otherwise fine.
    if (isBlockedPortrait && status !== 'error') {
      status = 'warning';
    }

    return { status, errors };
  }

  // Display creative
  const auditStatuses = creative.get('audit_statuses') ?? [];

  // Add audit status errors if present
  if (auditStatuses.length > 0) {
    const displayStatus = getDisplayCreativeStatus(creative, t);
    if (displayStatus.tooltip && displayStatus.status !== 'ready') {
      // Split by newlines so each auditor entry renders on its own line
      errors.push(...displayStatus.tooltip.split('\n'));
    }

    // Determine status: validation errors take priority, then audit status
    if (hasValidationErrors) {
      return { status: 'error', errors };
    }
    return { status: displayStatus.status, errors };
  }

  // No audit_statuses yet
  if (hasValidationErrors) {
    return { status: 'error', errors };
  }
  // If published, show pending; otherwise hide status
  if (campaign.get('state') === CampaignState.Published) {
    errors.push(t('Pending review'));
    return { status: 'warning', errors };
  }
  return { status: undefined, errors };
}

/**
 * Collects field-level validation errors for a single creative.
 */
function getValidationErrors(
  creative: DisplayCreative,
  campaign: Campaign,
  t: TFunction,
): string[] {
  const errors: string[] = toJS(
    creative.validate(['width', 'height', 'spec', 'destination_url', 'name'], false).errors,
  );

  const validDimensions = creative.getValidDimensions(campaign);
  const specs = Object.values(validDimensions).map((v) => v.spec);
  const creativeIsValidDimensions = specs.includes(creative.get('spec'));
  const creativeHasValidAspectRatio = hasValidAspectRatio(creative, validDimensions);

  if (!creativeIsValidDimensions && !creativeHasValidAspectRatio) {
    errors.push(
      `${creative.get('width')}x${creative.get('height')} does not match any standard ad size. Images must have exact aspect ratios and meet minimum dimensions.`,
    );
  }

  return errors.concat(validateAdTagCreative(creative, t));
}

/**
 * Maps a Bannersnack banner URL type to its human-readable file label.
 *
 * @param type - The banner URL type (e.g. `'jpg'`, `'animated-gif'`)
 * @returns The display label (e.g. `'JPG'`, `'GIF'`)
 */
function getBannersnackFileLabel(type: IBannersnackUrl['type']): string {
  return type === 'animated-gif' ? 'GIF' : type.toUpperCase();
}

/**
 * Builds download menu items for a Bannersnack creative's rendered assets.
 *
 * JPG, PNG, and PDF are always offered; MP4 and animated GIF are added only for
 * animated creatives. Each item links directly to the hosted asset and opens in
 * a new tab. Labels are just the file type (e.g. `'JPG'`) since the menu groups
 * them under a "Download" heading. Returns an empty array for non-Bannersnack
 * creatives.
 *
 * @param creative - The creative to build download items for
 * @returns Menu items for the creative card 3-dot menu (empty when not Bannersnack)
 */
export function getBannersnackDownloadMenuItems(
  creative: DisplayCreative,
): ICreativeCardMenuItem[] {
  if (!isBannersnackCreative(creative)) {
    return [];
  }

  const animated = creative.get('animated');
  const types: Array<IBannersnackUrl['type']> = animated
    ? ['jpg', 'png', 'pdf', 'mp4', 'animated-gif']
    : ['jpg', 'png', 'pdf'];
  const bannerUrls = creative.get('banner_urls') ?? [];

  return types.map((type) => {
    const url = bannerUrls.find((bannerUrl) => bannerUrl.type === type)?.url;
    return {
      label: getBannersnackFileLabel(type),
      href: url,
      target: '_blank',
      prefix: faFileDownload,
      disabled: creative.isUpdating || !url,
    };
  });
}
